import type {
  QuoteRulesetDetailPublic,
  QuoteRulesetVersionApplicabilityDetailPublic,
  UserBasicInfo,
} from "@common/client"
import { customerOption } from "@common/components/form/lookup/customer"
import { z } from "zod"
import type { GroupLabel } from "@/lib/grouping"

export const GROUPINGS = ["customer", "asset"] as const

export const groupingSchema = z.enum(GROUPINGS)

export type Grouping = z.infer<typeof groupingSchema>

export const GROUPING_LABELS: Record<Grouping, string> = {
  customer: "Customer",
  asset: "Asset",
}

export const RULE_PLACEHOLDER =
  "Write the rule as a sentence, e.g. “Quote service bulletins as optional items unless they are mandatory.”"

type Applicability = QuoteRulesetVersionApplicabilityDetailPublic

function applicability(playbook: QuoteRulesetDetailPublic): Applicability[] {
  return playbook.latest_version?.active_applicability ?? []
}

function customers(rows: Applicability[]): GroupLabel[] {
  return rows.flatMap(({ customer, customer_id }) => {
    if (customer) {
      return [{ key: customer.id, label: customerOption(customer).label }]
    }
    return customer_id
      ? [{ key: customer_id, label: "Restricted customer" }]
      : []
  })
}

function assets(rows: Applicability[]): GroupLabel[] {
  return rows.flatMap((row) => {
    const variant = row.type_certificate_model_variant
    if (variant) {
      return [{ key: variant.id, label: variant.marketing_name }]
    }
    if (row.part) {
      return [{ key: row.part.id, label: row.part.number }]
    }
    return row.part_id ? [{ key: row.part_id, label: "Restricted part" }] : []
  })
}

function sorted(subjects: GroupLabel[]): GroupLabel[] {
  return [...subjects].sort(
    (a, b) => a.label.localeCompare(b.label) || a.key.localeCompare(b.key),
  )
}

function joinedLabel(subjects: GroupLabel[]): string {
  return sorted(subjects)
    .map(({ label }) => label)
    .join(", ")
}

function joinedGroup(subjects: GroupLabel[], fallback: string): GroupLabel {
  return {
    key: sorted(subjects)
      .map(({ key }) => key)
      .join(","),
    label: joinedLabel(subjects) || fallback,
  }
}

export function customerNames(rows: Applicability[]): string {
  return joinedLabel(customers(rows))
}

export function assetNames(rows: Applicability[]): string {
  return joinedLabel(assets(rows))
}

export function customerLabel(playbook: QuoteRulesetDetailPublic): string {
  return customerNames(applicability(playbook))
}

export function assetLabel(playbook: QuoteRulesetDetailPublic): string {
  return assetNames(applicability(playbook))
}

export function playbookGroup(
  playbook: QuoteRulesetDetailPublic,
  grouping: Grouping,
): GroupLabel {
  switch (grouping) {
    case "customer":
      return joinedGroup(customers(applicability(playbook)), "All customers")
    case "asset":
      return joinedGroup(assets(applicability(playbook)), "All assets")
  }
}

export function playbookHistory(playbook: QuoteRulesetDetailPublic): {
  created_at: string
  created_by: UserBasicInfo | null
  updated_at: string
  last_modified_by: UserBasicInfo | null
} {
  const version = playbook.latest_version
  const revisedLater =
    version && new Date(version.created_at) > new Date(playbook.updated_at)
  return revisedLater
    ? {
        ...playbook,
        updated_at: version.created_at,
        last_modified_by: version.last_modified_by,
      }
    : playbook
}
